import React, { useMemo, useState } from 'react';
import { Calendar as CalendarIcon, List, ChevronLeft, ChevronRight, Moon, Sun, AlertCircle, Info, Star, ArrowRight } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
interface CalendarPageProps {
  onNavigate: (page: string) => void;
}
// Helper to format month and year
const formatMonthYear = (date: Date) => {
  const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
};
// Generate calendar grid days
const getDaysInMonth = (year: number, month: number) => {
  const date = new Date(year, month, 1);
  const days = [];
  // Get day of week of the 1st (0 = Sunday, 1 = Monday, etc.)
  // Adjust so Monday is 0, Sunday is 6
  let firstDay = date.getDay() - 1;
  if (firstDay === -1) firstDay = 6;
  // Add empty slots for days before the 1st
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  // Add days of the month
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};
export function CalendarPage({
  onNavigate
}: CalendarPageProps) {
  const [view, setView] = useState<'month' | 'list'>('list');
  const [currentDate, setCurrentDate] = useState(new Date(2024, 9, 1)); // Start at October 2024 — month with most events in demo data
  const [activeTooltipDay, setActiveTooltipDay] = useState<number | null>(null);
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };
  // Current transits mock data
  const currentTransits = [{
    planet: '☉ Солнце',
    sign: '♓ Рыбы',
    degree: "10°13'",
    element: 'water'
  }, {
    planet: '☽ Луна',
    sign: '♒ Водолей',
    degree: "6°40'",
    element: 'air'
  }, {
    planet: '☿ Меркурий',
    sign: '♓ Рыбы',
    degree: "22°02'",
    element: 'water'
  }, {
    planet: '♀ Венера',
    sign: '♈ Овен',
    degree: "22°59'",
    element: 'fire'
  }, {
    planet: '♂ Марс',
    sign: '♋ Рак',
    degree: "28°36'",
    element: 'water'
  }, {
    planet: '♃ Юпитер',
    sign: '♋ Рак',
    degree: "15°15'",
    element: 'water'
  }, {
    planet: '♄ Сатурн',
    sign: '♈ Овен',
    degree: "1°42'",
    element: 'fire'
  }];
  const elementColors: Record<string, string> = {
    fire: 'text-red-600',
    earth: 'text-emerald-600',
    air: 'text-indigo-600',
    water: 'text-sky-600' // Fixed from amber to sky
  };
  const allEvents = [
  // September 2024
  {
    id: 10,
    dateStr: '2024-09-02',
    displayDate: '2 Сентября',
    type: 'Новолуние',
    title: 'Новолуние в Деве',
    desc: 'Время для наведения порядка, планирования и внимания к здоровью.',
    icon: <Moon className="w-5 h-5" data-id="element-19637" />,
    color: 'text-emerald-500',
    bg: 'bg-emerald-100'
  }, {
    id: 11,
    dateStr: '2024-09-18',
    displayDate: '18 Сентября',
    type: 'Затмение',
    title: 'Лунное затмение в Рыбах',
    desc: 'Завершение эмоциональных циклов, раскрытие тайн и интуитивных прозрений.',
    icon: <Moon className="w-5 h-5" data-id="element-19638" />,
    color: 'text-indigo-500',
    bg: 'bg-indigo-100'
  },
  // October 2024
  {
    id: 1,
    dateStr: '2024-10-02',
    displayDate: '2 Октября',
    type: 'Затмение',
    title: 'Солнечное затмение в Весах',
    desc: 'Время кармических изменений в сфере партнерства и отношений.',
    icon: <Sun className="w-5 h-5" data-id="element-19639" />,
    color: 'text-amber-500',
    bg: 'bg-amber-100'
  }, {
    id: 4,
    dateStr: '2024-10-09',
    displayDate: '9 Октября',
    type: 'Ретроград',
    title: 'Юпитер становится ретроградным',
    desc: 'Переоценка социальных связей, образования и мировоззрения.',
    icon: <AlertCircle className="w-5 h-5" data-id="element-19640" />,
    color: 'text-rose-500',
    bg: 'bg-rose-100'
  }, {
    id: 5,
    dateStr: '2024-10-17',
    displayDate: '17 Октября',
    type: 'Полнолуние',
    title: 'Суперлуние в Овне',
    desc: 'Мощный всплеск энергии, кульминация личных проектов и инициатив.',
    icon: <Moon className="w-5 h-5" data-id="element-19641" />,
    color: 'text-rose-500',
    bg: 'bg-rose-100'
  },
  // November 2024
  {
    id: 6,
    dateStr: '2024-11-01',
    displayDate: '1 Ноября',
    type: 'Новолуние',
    title: 'Новолуние в Скорпионе',
    desc: 'Глубокие трансформации, работа с подсознанием и финансами.',
    icon: <Moon className="w-5 h-5" data-id="element-19642" />,
    color: 'text-purple-500',
    bg: 'bg-purple-100'
  }, {
    id: 7,
    dateStr: '2024-11-15',
    displayDate: '15 Ноября',
    type: 'Ингрессия',
    title: 'Сатурн выходит из ретрограда',
    desc: 'Ускорение в делах, связанных с карьерой, структурой и ответственностью.',
    icon: <Star className="w-5 h-5" data-id="element-19643" />,
    color: 'text-emerald-500',
    bg: 'bg-emerald-100'
  }, {
    id: 8,
    dateStr: '2024-11-26',
    displayDate: '26 Ноября',
    type: 'Ретроград',
    title: 'Ретроградный Меркурий в Стрельце',
    desc: 'Задержки в путешествиях, обучении и юридических вопросах.',
    icon: <AlertCircle className="w-5 h-5" data-id="element-19644" />,
    color: 'text-rose-500',
    bg: 'bg-rose-100'
  },
  // December 2024
  {
    id: 9,
    dateStr: '2024-12-06',
    displayDate: '6 Декабря',
    type: 'Ретроград',
    title: 'Ретроградный Марс во Льве',
    desc: 'Снижение энергии, пересмотр творческих проектов и способов самовыражения.',
    icon: <AlertCircle className="w-5 h-5" data-id="element-19645" />,
    color: 'text-rose-500',
    bg: 'bg-rose-100'
  }, {
    id: 3,
    dateStr: '2024-12-15',
    displayDate: '15 Декабря',
    type: 'Ингрессия',
    title: 'Меркурий выходит из ретрограда',
    desc: 'Улучшение коммуникаций, зеленая улица для новых договоренностей.',
    icon: <Star className="w-5 h-5" data-id="element-19646" />,
    color: 'text-emerald-500',
    bg: 'bg-emerald-100'
  }, {
    id: 12,
    dateStr: '2024-12-30',
    displayDate: '30 Декабря',
    type: 'Новолуние',
    title: 'Новолуние в Козероге',
    desc: 'Идеальное время для постановки долгосрочных целей на новый год.',
    icon: <Moon className="w-5 h-5" data-id="element-19647" />,
    color: 'text-slate-500',
    bg: 'bg-slate-100'
  }];
  // Filter events for current month
  const currentMonthEvents = useMemo(() => {
    return allEvents.filter(event => {
      const eventDate = new Date(event.dateStr);
      return eventDate.getMonth() === currentDate.getMonth() && eventDate.getFullYear() === currentDate.getFullYear();
    }).sort((a, b) => new Date(a.dateStr).getTime() - new Date(b.dateStr).getTime());
  }, [currentDate]);
  const calendarDays = useMemo(() => {
    return getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());
  }, [currentDate]);
  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-19648">
      <div className="bg-white border-b border-slate-200 pt-6 pb-8" data-id="element-19649">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-19650">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Астро-календарь'
        }]} className="mb-6" data-id="element-19651" />

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-19652">
            <div data-id="element-19653">
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" data-id="element-19654">
                Астрологический календарь
              </h1>
              <p className="text-slate-600" data-id="element-19655">
                Важные небесные события и их влияние на жизнь
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-19656">
              {/* Moon void of course widget */}
              <div className="flex items-center flex-wrap bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium border border-indigo-100" data-id="element-19657">
                <Moon className="w-4 h-4 mr-1.5 sm:mr-2 text-indigo-500" data-id="element-19658" />
                <span data-id="element-19659">Луна без курса:</span>
                <span className="ml-1.5 sm:ml-2 font-bold" data-id="element-19660">14:56 - 18:30</span>
                <div className="group relative ml-2 cursor-help" data-id="element-19661">
                  <Info className="w-4 h-4 text-indigo-400" data-id="element-19662" />
                  <div className="absolute bottom-full right-0 sm:left-1/2 sm:transform sm:-translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-19663">
                    Период неэффективности. Не рекомендуется начинать новые
                    важные дела.
                  </div>
                </div>
              </div>

              <div className="flex bg-slate-100 p-1 rounded-xl w-fit" data-id="element-19664">
                <button onClick={() => setView('month')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${view === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-19665">
                  <CalendarIcon className="w-4 h-4 mr-2" data-id="element-19666" /> Месяц
                </button>
                <button onClick={() => setView('list')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${view === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-19667">
                  <List className="w-4 h-4 mr-2" data-id="element-19668" /> Список
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 sm:mt-12" data-id="element-19669">
        {/* Moon Void-of-Course Widget + Current Transits */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-10" data-id="element-19670">
          {/* Moon Status */}
          <Card padding="md" className="border-amber-200 bg-amber-50/50 lg:col-span-1 self-start" data-id="element-19671">
            <div className="flex items-start gap-3" data-id="element-19672">
              <div className="p-2 rounded-xl bg-amber-100 text-amber-600" data-id="element-19673">
                <Moon className="w-5 h-5" data-id="element-19674" />
              </div>
              <div data-id="element-19675">
                <div className="font-bold text-slate-900 mb-1" data-id="element-19676">
                  Луна без курса
                </div>
                <div className="text-sm text-slate-700 mb-2" data-id="element-19677">
                  ☽ Луна в Водолее • до{' '}
                  <span className="font-semibold" data-id="element-19678">14:56 МСК</span>
                </div>
                <div className="flex items-start gap-1.5 text-xs text-slate-500" data-id="element-19679">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-19680" />
                  <span data-id="element-19681">
                    Не рекомендуется начинать важные дела, подписывать документы
                    и делать крупные покупки.
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Current Transits */}
          <Card padding="none" className="lg:col-span-2 overflow-hidden" data-id="element-19682">
            <div className="px-4 py-3 bg-slate-50/80 border-b border-slate-100 flex items-center justify-between" data-id="element-19683">
              <div className="font-bold text-slate-900 text-sm" data-id="element-19684">
                Текущие транзиты
              </div>
              <div className="text-xs text-slate-500" data-id="element-19685">Сегодня, 22:46 МСК</div>
            </div>
            <div className="divide-y divide-slate-100" data-id="element-19686">
              {currentTransits.map((t, i) => <div key={i} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/50 transition-colors" data-id="element-19687">
                  <span className="text-xs sm:text-sm text-slate-500 w-24 sm:w-28 flex-shrink-0" data-id="element-19688">
                    {t.planet}
                  </span>
                  <span className={`font-bold text-xs sm:text-sm ${elementColors[t.element]} flex-1 text-center`} data-id="element-19689">
                    {t.sign}
                  </span>
                  <span className="text-[10px] sm:text-xs text-slate-400 font-mono w-14 sm:w-16 text-right" data-id="element-19690">
                    {t.degree}
                  </span>
                </div>)}
            </div>
          </Card>
        </div>

        <div className="flex items-center justify-between mb-4 sm:mb-8" data-id="element-19691">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 capitalize" data-id="element-19692">
            {formatMonthYear(currentDate)}
          </h2>
          <div className="flex space-x-2" data-id="element-19693">
            <Button variant="outline" size="sm" icon={<ChevronLeft className="w-4 h-4" data-id="element-19695" />} onClick={prevMonth} data-id="element-19694" />
            <Button variant="outline" size="sm" icon={<ChevronRight className="w-4 h-4" data-id="element-19697" />} onClick={nextMonth} data-id="element-19696" />
          </div>
        </div>

        {view === 'list' ? <div className="space-y-4" data-id="element-19698">
            {currentMonthEvents.length > 0 ? currentMonthEvents.map(event => <Card key={event.id} hoverable padding="lg" className="flex flex-col sm:flex-row gap-6 cursor-pointer" onClick={() => onNavigate('calendar-event')} data-id="element-19699">
                  <div className="flex-shrink-0 text-center sm:text-left sm:w-32" data-id="element-19700">
                    <div className="text-sm text-slate-500 font-medium" data-id="element-19701">
                      {event.displayDate.split(' ')[1]}
                    </div>
                    <div className="text-3xl font-bold text-slate-900" data-id="element-19702">
                      {event.displayDate.split(' ')[0]}
                    </div>
                  </div>
                  <div className="flex-grow" data-id="element-19703">
                    <div className="flex items-center gap-3 mb-2" data-id="element-19704">
                      <div className={`p-1.5 rounded-lg ${event.bg} ${event.color}`} data-id="element-19705">
                        {event.icon}
                      </div>
                      <Badge variant="neutral" data-id="element-19706">{event.type}</Badge>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-19707">
                      {event.title}
                    </h3>
                    <p className="text-slate-600" data-id="element-19708">{event.desc}</p>
                  </div>
                  <div className="flex-shrink-0 flex items-center justify-center sm:justify-end" data-id="element-19709">
                    <Button variant="ghost" className="hidden sm:flex" onClick={e => {
              e.stopPropagation();
              onNavigate('calendar-event');
            }} data-id="element-19710">
                      Подробнее
                    </Button>
                  </div>
                </Card>) : <Card padding="xl" className="text-center text-slate-500" data-id="element-19711">
                <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" data-id="element-19712" />
                <p className="text-lg font-medium text-slate-900 mb-2" data-id="element-19713">
                  Нет важных событий
                </p>
                <p data-id="element-19714">В этом месяце нет значимых астрологических событий.</p>
              </Card>}
          </div> : <Card padding="none" className="overflow-hidden overflow-y-visible" onClick={() => setActiveTooltipDay(null)} data-id="element-19715">
            <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200" data-id="element-19716">
              {weekDays.map((day, i) => <div key={i} className="py-2 sm:py-3 text-center text-[11px] sm:text-sm font-semibold text-slate-600" data-id="element-19717">
                  {day}
                </div>)}
            </div>
            <div className="grid grid-cols-7 auto-rows-[56px] sm:auto-rows-[100px] md:auto-rows-[120px] divide-x divide-y divide-slate-100" data-id="element-19718">
              {calendarDays.map((date, i) => {
            if (!date) {
              return <div key={`empty-${i}`} className="bg-slate-50/50" data-id="element-19719"></div>;
            }
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            const dayEvents = currentMonthEvents.filter(e => e.dateStr === dateStr);
            const isToday = new Date().toDateString() === date.toDateString();
            return <div key={i} className={`group/day p-1 sm:p-2 relative transition-colors ${dayEvents.length > 0 ? 'hover:bg-slate-50 cursor-pointer' : ''} ${isToday ? 'bg-indigo-50/30' : ''}`} onClick={e => {
              if (dayEvents.length > 0) {
                e.stopPropagation();
                setActiveTooltipDay(activeTooltipDay === i ? null : i);
              }
            }} data-id="element-19720">
                    <div className={`text-xs sm:text-sm font-medium w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full mb-0.5 sm:mb-1 ${isToday ? 'bg-indigo-600 text-white' : 'text-slate-700'}`} data-id="element-19721">
                      {date.getDate()}
                    </div>

                    {/* Mobile: colored dots only */}
                    <div className="flex gap-0.5 flex-wrap sm:hidden" data-id="element-19722">
                      {dayEvents.map(event => <div key={event.id} className={`w-1.5 h-1.5 rounded-full ${event.bg.replace('100', '400')}`} data-id="element-19723" />)}
                    </div>

                    {/* Desktop: event labels */}
                    <div className="hidden sm:block space-y-1 overflow-y-auto hide-scrollbar max-h-[60px] md:max-h-[80px]" data-id="element-19724">
                      {dayEvents.map(event => <div key={event.id} className={`text-[10px] md:text-xs px-1.5 py-0.5 md:py-1 rounded truncate ${event.bg} ${event.color} font-medium`} title={event.title} data-id="element-19725">
                          {event.title}
                        </div>)}
                    </div>

                    {/* Tap/hover tooltip for days with events */}
                    {dayEvents.length > 0 && activeTooltipDay === i && <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-30 animate-in fade-in duration-150" onClick={e => e.stopPropagation()} data-id="element-19726">
                        <div className="bg-slate-900 text-white text-[11px] sm:text-xs rounded-xl shadow-xl px-3 py-2 min-w-[140px] max-w-[200px]" data-id="element-19727">
                          {dayEvents.map((event, idx) => <div key={event.id} className={`${idx > 0 ? 'mt-1.5 pt-1.5 border-t border-slate-700' : ''}`} data-id="element-19728">
                              <div className="font-medium truncate" data-id="element-19729">
                                {event.title}
                              </div>
                              <div className="text-slate-400 text-[10px] mt-0.5" data-id="element-19730">
                                {event.type}
                              </div>
                            </div>)}
                          <button onClick={e => {
                    e.stopPropagation();
                    setActiveTooltipDay(null);
                    onNavigate('calendar-event');
                  }} className="mt-2 pt-1.5 border-t border-slate-700 text-indigo-300 hover:text-indigo-200 font-medium text-[11px] flex items-center gap-1 w-full transition-colors" data-id="element-19731">
                            Подробнее <ArrowRight className="w-3 h-3" data-id="element-19732" />
                          </button>
                          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-slate-900" data-id="element-19733" />
                        </div>
                      </div>}
                  </div>;
          })}
            </div>
          </Card>}

        {/* Cross-links */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-19734">
          <button onClick={() => onNavigate('daily-horoscope')} className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all text-left group" data-id="element-19735">
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-2xl flex-shrink-0 group-hover:bg-amber-100 transition-colors" data-id="element-19736">
              ☀️
            </div>
            <div data-id="element-19737">
              <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-19738">
                Гороскоп на сегодня
              </div>
              <div className="text-sm text-slate-500" data-id="element-19739">
                Персональный прогноз для вашего знака
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('moon-calendar')} className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all text-left group" data-id="element-19740">
            <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-2xl flex-shrink-0 group-hover:bg-indigo-100 transition-colors" data-id="element-19741">
              🌙
            </div>
            <div data-id="element-19742">
              <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-19743">
                Лунный календарь
              </div>
              <div className="text-sm text-slate-500" data-id="element-19744">
                Фазы луны и рекомендации на день
              </div>
            </div>
          </button>
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-br from-slate-900 to-indigo-950 rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden shadow-xl" data-id="element-19745">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-19746" />
          <div className="relative z-10" data-id="element-19747">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" data-id="element-19748">
              Как эти события повлияют на вас?
            </h2>
            <p className="text-indigo-200 mb-8 max-w-xl mx-auto" data-id="element-19749">
              Узнайте, какие сферы вашей жизни затронут предстоящие затмения и
              ретрограды. Закажите персональный прогноз транзитов.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-19750">
              <button onClick={() => onNavigate('service-forecast')} className="px-8 py-3 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors" data-id="element-19751">
                Заказать прогноз транзитов
              </button>
              <button onClick={() => onNavigate('astrologers')} className="px-8 py-3 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/10 transition-colors" data-id="element-19752">
                Консультация астролога
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}